import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Pin, Megaphone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase F.3 — Portal Posts feed (pinned + scrollable feed).
 *
 * Used inside the Posts tab of both ClientDashboard and EmployeeDashboard.
 * Calls GET /api/portal/my-posts (portal-user scoped) and renders pinned posts
 * sticky at the top with a feed below.
 */
export default function PortalPostsFeed() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ pinned: [], feed: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/portal/my-posts`);
        if (!cancelled) setData(res.data || { pinned: [], feed: [] });
      } catch (e) { /* silent */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <Card><CardContent className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading announcements…
    </CardContent></Card>;
  }

  const noPosts = (!data.pinned || data.pinned.length === 0) && (!data.feed || data.feed.length === 0);
  if (noPosts) {
    return (
      <Card data-testid="portal-posts-card">
        <CardContent className="p-8 text-center text-muted-foreground" data-testid="portal-posts-empty">
          <Megaphone className="h-10 w-10 mx-auto mb-2 opacity-40" />
          No announcements right now.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="portal-posts-card">
      {(data.pinned || []).map((p) => (
        <Card key={p.id} className="border-amber-300 bg-amber-50/30" data-testid={`portal-post-pinned-${p.id}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Pin className="h-4 w-4 text-amber-600" />
              {p.title}
              <Badge variant="outline" className="border-amber-300 text-amber-700">Pinned</Badge>
            </CardTitle>
            <CardDescription>{new Date(p.created_at).toLocaleDateString()}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: p.body_html }} />
          </CardContent>
        </Card>
      ))}
      {(data.feed || []).map((p) => (
        <Card key={p.id} data-testid={`portal-post-${p.id}`}>
          <CardHeader>
            <CardTitle className="text-base">{p.title}</CardTitle>
            <CardDescription>{new Date(p.created_at).toLocaleDateString()}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: p.body_html }} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
